import {
  CopyOutlined,
  DeleteOutlined,
  MoreOutlined,
  SettingOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import { Button, Dropdown, type MenuProps, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../components/EntityTable/surfaces.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import type {
  ReportAccessLevel,
  ReportListItem,
  ReportVisibility
} from "../types/types.ts"

const { Text } = Typography

const VISIBILITY_COLOR: Record<ReportVisibility, string> = {
  private: "default",
  team: "blue",
  company: "green"
}

const ACCESS_COLOR: Record<ReportAccessLevel, string> = {
  owner: "green",
  edit: "blue",
  view: "default"
}

const accessLevelOf = (report: ReportListItem): ReportAccessLevel =>
  report.access_level ?? "owner"

interface ReportsListProps {
  reports: ReportListItem[]
  // Forwarded to EntityTable so a filter change drops the row selection.
  selectionFilters?: unknown
  loading: boolean
  onOpen: (report: ReportListItem) => void
  onToggleFavorite: (report: ReportListItem) => void
  onSettings: (report: ReportListItem) => void
  onDuplicate: (report: ReportListItem) => void
  onDelete: (report: ReportListItem) => void
}

export const ReportsList = ({
  reports,
  selectionFilters,
  loading,
  onOpen,
  onToggleFavorite,
  onSettings,
  onDuplicate,
  onDelete
}: ReportsListProps) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()

  const rowMenu = useMemo(
    () =>
      (record: ReportListItem): MenuProps["items"] => {
        const owned = accessLevelOf(record) === "owner"
        // Settings writes the same columns PATCH allows an editor to write;
        // only deletion stays with the owner.
        const editable = accessLevelOf(record) !== "view"
        return [
          ...(editable
            ? [
                {
                  key: "settings",
                  icon: <SettingOutlined />,
                  label: t("reports.actions.settings"),
                  onClick: () => onSettings(record)
                }
              ]
            : []),
          {
            key: "duplicate",
            icon: <CopyOutlined />,
            label: t("reports.actions.duplicate"),
            onClick: () => onDuplicate(record)
          },
          ...(owned
            ? [
                {
                  key: "delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  label: t("reports.actions.delete"),
                  onClick: () => onDelete(record)
                }
              ]
            : [])
        ]
      },
    [t, onSettings, onDuplicate, onDelete]
  )

  const columns = useMemo<ColumnsType<ReportListItem>>(
    () => [
      {
        key: "favorite",
        width: 40,
        align: "center",
        render: (_value, record) => (
          <Button
            type="text"
            size="small"
            aria-label={
              record.is_favorite
                ? t("reports.actions.unfavorite")
                : t("reports.actions.favorite")
            }
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(record)
            }}
            icon={
              record.is_favorite ? (
                <StarFilled style={{ color: "#faad14" }} />
              ) : (
                <StarOutlined style={{ color: "#d9d9d9" }} />
              )
            }
          />
        )
      },
      {
        title: t("reports.columns.name"),
        dataIndex: "name",
        key: "name",
        render: (value: string) => <Text strong>{value}</Text>
      },
      {
        title: t("reports.columns.owner"),
        key: "owner",
        render: (_value, record) => getEntityName(record.owner)
      },
      {
        title: t("reports.columns.visibility"),
        dataIndex: "visibility",
        key: "visibility",
        render: (value: ReportVisibility) => (
          <Tag color={VISIBILITY_COLOR[value]}>
            {t(`reports.visibility.${value}`)}
          </Tag>
        )
      },
      {
        title: t("reports.columns.access"),
        key: "access",
        render: (_value, record) => {
          const level = accessLevelOf(record)
          return (
            <Tag color={ACCESS_COLOR[level]}>{t(`reports.access.${level}`)}</Tag>
          )
        }
      },
      {
        title: t("reports.columns.widgets"),
        dataIndex: "widget_count",
        key: "widget_count",
        align: "center"
      },
      {
        title: t("reports.columns.updated"),
        dataIndex: "updated_at",
        key: "updated_at",
        render: (value: string) => formatDate(value) || empty
      },
      {
            width: 48,
        align: "center",
        render: (_value, record) => (
          // Dropdown menus portal out of the row, but React re-bubbles their
          // clicks through the component tree; stop them here so a menu action
          // never also triggers the row's open handler.
          <span onClick={(e) => e.stopPropagation()}>
            <Dropdown menu={{ items: rowMenu(record) }} trigger={["click"]}>
              <Button
                type="text"
                size="small"
                aria-label={t("reports.actions.menu")}
                icon={<MoreOutlined />}
              />
            </Dropdown>
          </span>
        )
      }
    ],
    [t, formatDate, onToggleFavorite, rowMenu]
  )

  return (
    <EntityTable<ReportListItem>
      surface={TABLE_SURFACES.reports}
      selectionMode="on-demand"
      selectionFilters={selectionFilters}
      tab={DEFAULT_TAB}
      rowKey="id"
      columns={columns}
      dataSource={reports}
      loading={loading}
      withOutPagination
          pagination={false}
      onRow={(record) => ({
        onClick: () => onOpen(record)
      })}
      rowClassName="cursor-pointer"
    />
  )
}
